import { useState, useEffect, useCallback } from 'react';
import { OfflineManager } from '../services/OfflineManager';
import { GameSession, GameResult, GameStatistics } from '../types';

export interface OfflineState {
  isOnline: boolean;
  isSinglePlayerAvailable: boolean;
  isMultiplayerAvailable: boolean;
  offlineGamesCount: number;
  hasOfflineProfile: boolean;
}

export interface OfflineActions {
  saveOfflineGame: (gameSession: GameSession) => Promise<void>;
  getOfflineGames: () => GameSession[];
  removeOfflineGame: (gameId: string) => void;
  recordGameResult: (gameResult: GameResult) => GameStatistics;
  getStatistics: () => GameStatistics;
  syncOfflineData: () => Promise<void>;
  clearOfflineData: () => void;
}

export function useOffline(): [OfflineState, OfflineActions] {
  const [offlineManager] = useState(() => new OfflineManager());
  const [offlineState, setOfflineState] = useState<OfflineState>(() => {
    const storageInfo = offlineManager.getOfflineStorageInfo();
    return {
      isOnline: offlineManager.getOnlineStatus(),
      isSinglePlayerAvailable: offlineManager.isSinglePlayerAvailable(),
      isMultiplayerAvailable: offlineManager.isMultiplayerAvailable(),
      offlineGamesCount: storageInfo.gamesCount,
      hasOfflineProfile: storageInfo.hasProfile
    };
  });

  // Update state when online status changes
  useEffect(() => {
    const unsubscribe = offlineManager.onOnlineStatusChange((isOnline) => {
      setOfflineState(prevState => ({
        ...prevState,
        isOnline,
        isMultiplayerAvailable: offlineManager.isMultiplayerAvailable()
      }));
    });

    return unsubscribe;
  }, [offlineManager]);

  // Update offline storage info periodically
  const updateStorageInfo = useCallback(() => {
    const storageInfo = offlineManager.getOfflineStorageInfo();
    setOfflineState(prevState => ({
      ...prevState,
      offlineGamesCount: storageInfo.gamesCount,
      hasOfflineProfile: storageInfo.hasProfile
    }));
  }, [offlineManager]);

  const saveOfflineGame = useCallback(async (gameSession: GameSession) => {
    await offlineManager.saveOfflineGame(gameSession);
    updateStorageInfo();
  }, [offlineManager, updateStorageInfo]);

  const getOfflineGames = useCallback(() => {
    return offlineManager.getOfflineGames();
  }, [offlineManager]);

  const removeOfflineGame = useCallback((gameId: string) => {
    offlineManager.removeOfflineGame(gameId);
    updateStorageInfo();
  }, [offlineManager, updateStorageInfo]);

  const recordGameResult = useCallback((gameResult: GameResult) => {
    const stats = offlineManager.recordGameResult(gameResult);
    updateStorageInfo();
    return stats;
  }, [offlineManager, updateStorageInfo]);

  const getStatistics = useCallback(() => {
    return offlineManager.getStatistics();
  }, [offlineManager]);

  const syncOfflineData = useCallback(async () => {
    await offlineManager.syncOfflineData();
    updateStorageInfo();
  }, [offlineManager, updateStorageInfo]);

  const clearOfflineData = useCallback(() => {
    offlineManager.clearOfflineData();
    updateStorageInfo();
  }, [offlineManager, updateStorageInfo]);

  const actions: OfflineActions = {
    saveOfflineGame,
    getOfflineGames,
    removeOfflineGame,
    recordGameResult,
    getStatistics,
    syncOfflineData,
    clearOfflineData
  };

  return [offlineState, actions];
}